/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './BeneficiaryTable.scss';

import { partition, maxBy, cloneDeep, isEmpty, omit, isEqual } from 'lodash';
import classnames from 'classnames';

import { array, string } from 'prop-types';
import React, { Fragment, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { InputField } from '@trp-beacon-ui-kit/react-controls';

import { Percent } from 'components/shared/Text/Text';
import CancelConfirmationModal from 'components/shared/CancelConfirmationModal/CancelConfirmationModal';
import FormattedInput from 'components/shared/FormattedInput/FormattedInput';
import ToolTip from 'components/shared/ToolTip/ToolTip';
import If from 'components/shared/If/If';
import Content, { contentConverter } from 'services/Content';
import {
  isEducationSavingsAccount,
  isTransferOnDeath
} from 'services/AccountTypes';
import {
  getPendingStatusByAccountType,
  getIsEditingBeneficiaries
} from 'state/beneficiaries/BeneficiarySelectors';
import { beneficiaryLockDisabled } from 'services/FeatureFlags';
import { footerLinks } from 'services/Externals';
import { ReactComponent as WarningCircle } from 'assets/icons/warning-circle.svg';
import FixedSubmitFooter from 'components/layouts/Preferences/shared/FixedSubmitFooter/FixedSubmitFooter';
import InvestmentsTable from './InvestmentTable';
import {
  isValidName,
  isValidNameLength,
  isValidEntityNameLength,
  runValidationFns
} from 'services/Validation';
import {
  getCustomer,
  getPrimaryEmailAddress
} from 'state/customer/CustomerSelectors';
import {
  getBeneficiaryDisplayBirthdate,
  getBeneficiaryDisplayName,
  getBeneficiaryDisplayTaxId,
  getBeneficiaryRelationshipName,
  isBeneficiaryPrimary,
  isBeneficiaryABusinessEntity,
  beneficiarySuffixOptions,
  beneficiaryTypeOptions,
  beneficiaryRelationshipOptions,
  successorBeneficiaryRelationshipOptions,
  newPrimaryBeneficiaryInitialState,
  newSecondaryBeneficiaryInitialState,
  generateBeneficiaryPayload,
  generateBenficiaryUpdatePayload,
  cleanBeneficiaryWTypeId,
  isDeleteAllBeneficiaries,
  generateDistributionError,
  isPrimaryBeneficiaryError,
  primaryBeneficiaryErrorText,
  isBeneficiariesValid,
  checkIsTodError,
  BENEFICIARY_EVENT_TYPES,
  buildBusinessEvent,
  isRegistrationPending,
  educationSavingsWarning,
  isSpouseError,
  isBeneficiaryASpouse,
  getHasAnyDobError
} from 'services/Beneficiary';
import {
  addBeneficiaries,
  deleteBeneficiaries,
  updateBeneficiaries,
  captureBeneficiaryBusinessEvent
} from 'services/Rest';
import { updateBeneEditFlag } from 'state/beneficiaries/BeneficiaryActions';
import Store from 'state/Store';
import ProfileStackedLabeledInput from 'components/layouts/Preferences/shared/ProfileStackedLabeledInput';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { ReactComponent as WarningTriangle } from 'assets/icons/warning-triangle.svg';
import { ReactComponent as Caution } from 'assets/icons/Caution.svg';
import BaseModal from 'components/shared/Modal/BaseModal';
import { isValidBeneficiaryDob } from 'services/Dates';
import { isSalesforceRepEnabled } from 'state/view/ViewSelectors';

//TODO: Still need to update edit fields for entity

const BeneficiaryName = ({ name }) => <Fragment>{name}</Fragment>;

const BeneficiarySSN = ({ ssn }) => <Fragment>{ssn}</Fragment>;

const BeneficiaryDOB = ({ dob }) => <Fragment>{dob}</Fragment>;

const AccountOwners = ({ owners }) => (
  <span className="beneficiary-owner-names">({owners})</span>
);

const BeneficiaryNameWithPermissions = WithUserPermissions(BeneficiaryName);
const BeneficiarySSNWithPermissions = WithUserPermissions(BeneficiarySSN);
const BeneficiaryDOBWithPermissions = WithUserPermissions(BeneficiaryDOB);
const AccountOwnersWithPermissions = WithUserPermissions(AccountOwners);

export const editAlignment = ({
  isEntity,
  individualStyle,
  beneficiary,
  entityStyle
}) => {
  if (isEntity) {
    if (entityStyle) {
      return 24;
    }
    return 0;
  }
  if (individualStyle || (beneficiary.firstName && beneficiary.lastName)) {
    return 24;
  }
  return 0;
};

export const BeneficiaryRow = ({
  beneficiary,
  isEditing,
  idx,
  handleFieldChangeEvent,
  handleSelectFieldChangeEvent,
  handleDistributionChangeEvent,
  handleDeleteBeneficiary,
  isDistributionError,
  isEducationSavings,
  hasSpouseError,
  hasValidationError
}) => {
  const cleanedDistributionAmount = Number(
    parseFloat(beneficiary.distributionAmount * 100).toPrecision(4)
  );
  const isEntity = isBeneficiaryABusinessEntity(beneficiary);
  const distributionClassname = classnames('distribution-input', {
    'distribution-error': isDistributionError
  });
  const relationshipClassname = classnames('select-element', {
    'relationship-error':
      (hasSpouseError && isBeneficiaryASpouse(beneficiary)) ||
      beneficiary.relationshipId === 0
  });
  const dobClassname = classnames('date-input', {
    'dob-error':
      !isValidBeneficiaryDob(beneficiary.birthDate) &&
      !isEmpty(beneficiary.birthDate)
  });

  const [individualStyle, setIndividualStyle] = useState(true);

  const [entityStyle, setEntityStyle] = useState(true);

  if (isEditing) {
    const columnClassname = isEntity
      ? 'entity-multi-edit'
      : 'multi-edit-column';
    return (
      <Fragment>
        <tr className="beneficiary-row" key={idx}>
          <td className="editing-column">
            <div className={columnClassname}>
              {!isEducationSavings && (
                <ProfileStackedLabeledInput validators={[]}>
                  <div className="float-container">
                    {beneficiary.typeId && (
                      <label htmlFor="typeId">Type*</label>
                    )}
                    <select
                      className="select-bene-type"
                      name="typeId"
                      id="typeId"
                      value={beneficiary.typeId}
                      onChange={(e) => {
                        handleSelectFieldChangeEvent(event, beneficiary);
                        setIndividualStyle(e.target.value !== 1);
                      }}>
                      {beneficiaryTypeOptions.map((type, idx) => (
                        <option key={idx} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </ProfileStackedLabeledInput>
              )}
              {!isEntity && (
                <Fragment>
                  <ProfileStackedLabeledInput validators={[]}>
                    <InputField
                      type="text"
                      data-testid="firstName"
                      value={beneficiary.firstName}
                      name="firstName"
                      id="firstName"
                      onChange={(event) => {
                        handleFieldChangeEvent(event, beneficiary);
                        setIndividualStyle(
                          event.target.value && beneficiary.lastName
                        );
                        hasValidationError(
                          [isValidName, isValidNameLength],
                          event
                        );
                      }}
                      title="First Name*"
                      validation={[isValidName, isValidNameLength]}
                      required
                    />
                  </ProfileStackedLabeledInput>
                  <ProfileStackedLabeledInput validators={[]}>
                    <InputField
                      value={beneficiary.middleName}
                      type="text"
                      name="middleName"
                      onChange={() =>
                        handleFieldChangeEvent(event, beneficiary)
                      }
                      title="Middle Name"
                      required={false}
                    />
                  </ProfileStackedLabeledInput>
                  <ProfileStackedLabeledInput validators={[]}>
                    <InputField
                      value={beneficiary.lastName}
                      data-testid="lastName"
                      type="text"
                      name="lastName"
                      onChange={(event) => {
                        handleFieldChangeEvent(event, beneficiary);
                        setIndividualStyle(
                          event.target.value && beneficiary.firstName
                        );
                        hasValidationError(
                          [isValidName, isValidNameLength],
                          event
                        );
                      }}
                      title="Last Name*"
                      validation={[isValidName, isValidNameLength]}
                      required
                    />
                  </ProfileStackedLabeledInput>
                  <ProfileStackedLabeledInput
                    className="suffix-input-container"
                    validators={[]}>
                    <div className="float-container">
                      <label htmlFor="suffix">Suffix</label>
                      <select
                        className="select-bene-type"
                        name="nameSuffix"
                        id="suffix"
                        value={beneficiary.nameSuffix}
                        onChange={() =>
                          handleFieldChangeEvent(event, beneficiary)
                        }>
                        {beneficiarySuffixOptions.map((type, idx) => (
                          <option key={idx} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </ProfileStackedLabeledInput>
                </Fragment>
              )}
              {isEntity && (
                <Fragment>
                  <ProfileStackedLabeledInput
                    validators={[]}
                    className="entity-profile-labeled-input">
                    <InputField
                      type="text"
                      value={beneficiary.businessEntityName}
                      name="businessEntityName"
                      onChange={(event) => {
                        handleFieldChangeEvent(event, beneficiary);
                        setEntityStyle(!!event.target.value);
                        hasValidationError(
                          [isValidName, isValidEntityNameLength],
                          event
                        );
                      }}
                      title="Name*"
                      validation={[isValidName, isValidEntityNameLength]}
                      required
                    />
                  </ProfileStackedLabeledInput>
                </Fragment>
              )}
            </div>
          </td>
          <td
            data-testid="relationshipColumn"
            className={
              isEntity
                ? 'editing-column entity-editing-column'
                : 'editing-column'
            }
            style={{
              paddingBottom: editAlignment({
                isEntity,
                individualStyle,
                beneficiary,
                entityStyle
              })
            }}>
            <div className="single-edit-column">
              <ProfileStackedLabeledInput
                className="suffix-input-container"
                validators={[]}>
                {!isEntity && !isEducationSavings && (
                  <div className="float-container">
                    <label htmlFor="relationshipId">Relationship*</label>
                    <select
                      className={relationshipClassname}
                      name="relationshipId"
                      id="relationshipId"
                      value={beneficiary.relationshipId}
                      onChange={() =>
                        handleSelectFieldChangeEvent(event, beneficiary)
                      }
                      placeholder="Select*">
                      {beneficiaryRelationshipOptions.map((type, idx) => (
                        <option key={idx} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!isEntity && isEducationSavings && (
                  <div className="float-container">
                    <label htmlFor="relationshipId">Relationship*</label>
                    <select
                      className={relationshipClassname}
                      name="relationshipId"
                      id="relationshipId"
                      value={beneficiary.relationshipId}
                      onChange={() =>
                        handleSelectFieldChangeEvent(event, beneficiary)
                      }
                      placeholder="Select*">
                      {successorBeneficiaryRelationshipOptions.map(
                        (type, idx) => (
                          <option key={idx} value={type.value}>
                            {type.label}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                )}
                {isEntity && <div className="disabled-text">Entity</div>}
              </ProfileStackedLabeledInput>
            </div>
          </td>
          <td
            className="editing-column"
            style={{
              paddingBottom: editAlignment({
                isEntity,
                individualStyle,
                beneficiary,
                entityStyle
              })
            }}>
            <div className="single-edit-column">
              <ProfileStackedLabeledInput validators={[]}>
                <div className="float-container">
                  <label htmlFor="taxId">SSN/TIN</label>
                  <FormattedInput
                    className="tax-id-input"
                    name="taxId"
                    id="taxId"
                    onChange={() => handleFieldChangeEvent(event, beneficiary)}
                    value={beneficiary.taxId}
                    format={isEntity ? 'tin' : 'ssn'}
                  />
                </div>
              </ProfileStackedLabeledInput>
            </div>
          </td>
          <td
            className={
              isEntity
                ? 'editing-column entity-editing-column'
                : 'editing-column'
            }
            style={{
              paddingBottom: editAlignment({
                isEntity,
                individualStyle,
                beneficiary,
                entityStyle
              })
            }}>
            <div className="single-edit-column">
              <ProfileStackedLabeledInput validators={[]}>
                {!isEntity && (
                  <div className="float-container">
                    <label htmlFor="birthDate">Date of Birth</label>
                    <FormattedInput
                      className={dobClassname}
                      name="birthDate"
                      id="birthDate"
                      onChange={() =>
                        handleFieldChangeEvent(event, beneficiary)
                      }
                      value={beneficiary.birthDate}
                      format={'birthday'}
                      placeholder={'MM/DD/YYYY'}
                    />
                  </div>
                )}
                {isEntity && <div className="disabled-text">N/A</div>}
              </ProfileStackedLabeledInput>
            </div>
          </td>
          <td
            className="editing-column"
            style={{
              paddingBottom: editAlignment({
                isEntity,
                individualStyle,
                beneficiary,
                entityStyle
              })
            }}>
            <div className="single-edit-column distribution-cell">
              <div className="float-container">
                <label htmlFor="distribution">Percent*</label>
                <span className="distribution-value">
                  <input
                    className={distributionClassname}
                    type="number"
                    min="1"
                    max="100"
                    name="distributionAmount"
                    id="distribution"
                    value={cleanedDistributionAmount}
                    onChange={() =>
                      handleDistributionChangeEvent(event, beneficiary)
                    }
                  />
                  <span
                    className={
                      isDistributionError ? 'error-percent' : 'percent'
                    }>
                    %
                  </span>
                </span>
                <span className="beneficiary-delete-button-container">
                  <button
                    className="beneficiary-delete-button"
                    onClick={() => handleDeleteBeneficiary(event, beneficiary)}>
                    Delete
                  </button>
                </span>
              </div>
            </div>
          </td>
        </tr>
        <tr className="delete-row-mobile">
          <td colSpan="5">
            <div className="mobile-beneficiary-delete-button-container">
              <button
                className="mobile-beneficiary-delete-button"
                onClick={() => handleDeleteBeneficiary(event, beneficiary)}>
                Delete
              </button>
            </div>
          </td>
        </tr>
      </Fragment>
    );
  }
  return (
    <tr className="beneficiary-row-read-only" key={idx}>
      <td
        data-title={
          isEducationSavings ? 'Successor Beneficiary Name' : 'Beneficiary Name'
        }>
        <BeneficiaryNameWithPermissions
          name={getBeneficiaryDisplayName(beneficiary)}
          shouldMask
        />
      </td>
      <td data-title="Beneficiary Type">
        {getBeneficiaryRelationshipName(beneficiary)}
      </td>
      <td data-title="SSN/TIN">
        <BeneficiarySSNWithPermissions
          ssn={getBeneficiaryDisplayTaxId(beneficiary)}
          shouldMask
        />
      </td>
      <td data-title="Date of Birth">
        <BeneficiaryDOBWithPermissions
          dob={getBeneficiaryDisplayBirthdate(beneficiary)}
          shouldMask
        />
      </td>
      <td data-title="% of Benefits" className="distribution-column">
        <Percent withDecimal={true}>{beneficiary.distributionAmount}</Percent>
      </td>
    </tr>
  );
};

                          
const DeletedRow = ({ beneficiary, undoDelete, key }) => {
  const typeText = isBeneficiaryPrimary(beneficiary)
    ? 'primary beneficiary'
    : 'secondary beneficiary';

  return (
    <tr className="beneficiary-deleted-row" key={key}>
      <td colSpan="5" className="blocked_data">
        {isBeneficiaryABusinessEntity(beneficiary)
          ? beneficiary.businessEntityName
          : beneficiary.name}{' '}
        will be deleted as {typeText} after you save your changes.
        <button
          className="undo-delete-button"
          onClick={() => undoDelete(event, beneficiary)}>
          Undo
        </button>
      </td>
    </tr>
  );
};

class BeneficiaryTable extends React.Component {
  state = {
    beneficiaries: cloneDeep(this.props.beneficiaries),
    isEditingBeneficiaries: false,
    hasEditError: false,
    showAccounts: false,
    showConfirmationModal: false
  };

  componentWillUnmount() {
    Store.dispatch(updateBeneEditFlag(false));
  }

  handleSubmitCancelConfirmation = () => {
    const { history } = this.props;
    history.push('/profile');
  };

  hasValidationError = (validations, event) => {
    const results = runValidationFns(validations, event);
    if (results) {
      const hasError = !results.every(
        (validationResult) => validationResult === true
      );
      this.setState({ hasEditError: hasError });
    }
  };

  handleSubmitCancel = () => {
    this.setState({ showConfirmationModal: true });
  };

  handleCancelConfirmation = () => {
    this.setState({ showConfirmationModal: false });
  };

  handleSave = () => {
    const { beneficiaries: currentBeneficiaries } = this.state;
    const {
      beneficiaries: previousBeneficiaries,
      accounts,
      customer: user,
      customerEmail
    } = this.props;
    const payload = generateBeneficiaryPayload(
      accounts,
      previousBeneficiaries,
      currentBeneficiaries
    );

    if (
      isDeleteAllBeneficiaries(currentBeneficiaries) ||
      isEmpty(currentBeneficiaries)
    ) {
      const event = buildBusinessEvent(
        BENEFICIARY_EVENT_TYPES.DELETE,
        customerEmail,
        user,
        accounts
      );
      deleteBeneficiaries(payload, () =>
        captureBeneficiaryBusinessEvent(event)
      );
    } else if (isEmpty(previousBeneficiaries)) {
      const event = buildBusinessEvent(
        BENEFICIARY_EVENT_TYPES.ADD,
        customerEmail,
        user,
        accounts
      );
      addBeneficiaries(payload, () => captureBeneficiaryBusinessEvent(event));
    } else {
      const updatedPayload = generateBenficiaryUpdatePayload(
        accounts,
        currentBeneficiaries
      );
      const event = buildBusinessEvent(
        BENEFICIARY_EVENT_TYPES.UPDATE,
        customerEmail,
        user,
        accounts
      );
      updateBeneficiaries(updatedPayload, () =>
        captureBeneficiaryBusinessEvent(event)
      );
    }
  };

  toggleEdit = () => {
    Store.dispatch(updateBeneEditFlag(true));
    this.setState({ isEditingBeneficiaries: true });
  };

  toggleDisplay = (showAccounts) =>
    this.setState({ showAccounts: !showAccounts });

  addNewPrimary = () => {
    const { beneficiaries } = this.state;
    let newBeneficiaries = cloneDeep(beneficiaries);
    if (beneficiaries.length) {
      const nextId = maxBy(newBeneficiaries, '_id')._id + 1;
      newBeneficiaries.push({
        ...newPrimaryBeneficiaryInitialState,
        _id: nextId
      });
    } else {
      newBeneficiaries.push({ ...newPrimaryBeneficiaryInitialState, _id: 0 });
    }
    this.setState({ beneficiaries: newBeneficiaries });
  };
  addNewSecondary = () => {
    const { beneficiaries } = this.state;
    let newBeneficiaries = cloneDeep(beneficiaries);
    if (beneficiaries.length) {
      const nextId = maxBy(newBeneficiaries, '_id')._id + 1;
      newBeneficiaries.push({
        ...newSecondaryBeneficiaryInitialState,
        _id: nextId
      });
    } else {
      newBeneficiaries.push({ ...newSecondaryBeneficiaryInitialState, _id: 0 });
    }
    this.setState({ beneficiaries: newBeneficiaries });
  };

  handleFieldChangeEvent = (event, beneficiary) => {
    const { beneficiaries } = this.state;
    let newBeneficiaries = cloneDeep(beneficiaries);
    if (event) {
      const { name, value } = event.target;
      let beneToEdit = newBeneficiaries.find(
        (bene) => bene._id === beneficiary._id
      );
      if (beneToEdit && name) {
        beneToEdit[name] = value;
      }
      this.setState({ beneficiaries: newBeneficiaries });
    }
  };

  handleDistributionChangeEvent = (event, beneficiary) => {
    const { beneficiaries } = this.state;
    let newBeneficiaries = cloneDeep(beneficiaries);
    const { name, value } = event.target;
    let newDistributionAmount = Number.parseFloat(value);
    if (newDistributionAmount > 100) {
      newDistributionAmount = 100;
    } else if (newDistributionAmount <= 0) {
      newDistributionAmount = 1;
    }
    newDistributionAmount = Number(
      parseFloat(newDistributionAmount / 100).toPrecision(4)
    );
    let beneToEdit = newBeneficiaries.find(
      (bene) => bene._id === beneficiary._id
    );
    if (beneToEdit && name) {
      beneToEdit[name] = newDistributionAmount;
    }
    this.setState({ beneficiaries: newBeneficiaries });
  };

  handleSelectFieldChangeEvent = (event, beneficiary) => {
    const { beneficiaries } = this.state;
    const { name, value } = event.target;
    const targetValue = parseInt(value);
    let beneToEdit = beneficiaries.find((bene) => bene._id === beneficiary._id);
    const editingBeneIndex = beneficiaries.findIndex(
      (bene) => bene._id === beneficiary._id
    );
    if (name === 'typeId') {
      beneToEdit = cleanBeneficiaryWTypeId(beneToEdit, value);
      beneficiaries[editingBeneIndex] = beneToEdit;
    }
    if (beneToEdit && name) {
      beneToEdit[name] = targetValue;
    }
    this.setState({ beneficiaries });
  };

  handleDeleteBeneficiary = (event, beneficiary) => {
    const { beneficiaries } = this.state;
    const { beneficiaries: initialBeneficiaries } = this.props;
    let newBeneficiaries = cloneDeep(beneficiaries);
    const beneExistedInitially = initialBeneficiaries.find(
      (bene) => bene._id === beneficiary._id
    );
    if (beneExistedInitially) {
      let beneToDelete = newBeneficiaries.find(
        (bene) => bene._id === beneficiary._id
      );
      const deleteBeneIndex = newBeneficiaries.findIndex(
        (bene) => bene._id === beneficiary._id
      );
      newBeneficiaries[deleteBeneIndex] = { ...beneToDelete, deleted: true };
    } else {
      newBeneficiaries = newBeneficiaries.filter(
        (bene) => bene._id !== beneficiary._id
      );
    }

    this.setState({ beneficiaries: newBeneficiaries });
  };

  handleUndoDelete = (event, beneficiary) => {
    const { beneficiaries } = this.state;
    let newBeneficiaires = cloneDeep(beneficiaries);

    let beneToUndo = newBeneficiaires.find(
      (bene) => bene._id === beneficiary._id
    );
    const undoBeneIndex = newBeneficiaires.findIndex(
      (bene) => bene._id === beneficiary._id
    );

    beneToUndo = omit(beneToUndo, ['deleted']);
    newBeneficiaires[undoBeneIndex] = beneToUndo;

    this.setState({ beneficiaries: newBeneficiaires });
  };

  checkIfDisableSubmit = (isAnyError) => {
    const { beneficiaries: currentBeneficiaries } = this.state;
    const { beneficiaries: previousBeneficiaries } = this.props;
    return (
      isEqual(currentBeneficiaries, previousBeneficiaries) ||
      isAnyError ||
      !isBeneficiariesValid(currentBeneficiaries)
    );
  };

  render() {
    const {
      accounts,
      isEditingBeneGlobal,
      accountType,
      disableAllActions,
      isEditable,
      isIraAccount
    } = this.props;
    const {
      beneficiaries,
      showAccounts,
      isEditingBeneficiaries,
      hasEditError,
      showConfirmationModal
    } = this.state;
   
    const owners = accounts?.[0]?.ownersFormatted;
    const [primaryBeneficiaries, secondaryBeneficiaries] = partition(
      beneficiaries,
      (bene) => isBeneficiaryPrimary(bene)
    );
    let pendingText = '';
    let isPending = false;
    const pendingStatus = isRegistrationPending(beneficiaries);
    if (pendingStatus) {
      pendingText = pendingStatus.pendingText;
      isPending = pendingStatus.isPending;
    }

    const hasDobError = getHasAnyDobError(
      beneficiaries,
      isEditingBeneficiaries
    );
    const isEducationSavings =
      accounts.length &&
      accounts[0] &&
      isEducationSavingsAccount(accounts[0].accountTypeId);
    const educationSavingsAgeWarning = educationSavingsWarning(
      beneficiaries,
      isEditingBeneficiaries,
      isEducationSavings
    );
    const hasSpouseError = isSpouseError(beneficiaries, isEditingBeneficiaries);
    const isTOD =
      accounts.length &&
      accounts[0] &&
      isTransferOnDeath(accounts[0].accountTypeId);
    const isTODError = checkIsTodError(beneficiaries, isTOD);
    const disableEdit =
      isEditingBeneficiaries || isEditingBeneGlobal || isPending;
    const primaryDistributionError = generateDistributionError(
      primaryBeneficiaries,
      true,
      isEducationSavings
    );
    const secondaryDistributionError = generateDistributionError(
      secondaryBeneficiaries
    );
    const primaryBeneficiaryError = isPrimaryBeneficiaryError(
      primaryBeneficiaries,
      secondaryBeneficiaries
    );
    const isAnyError =
      hasEditError ||
      !primaryDistributionError.isValid ||
      !secondaryDistributionError.isValid ||
      primaryBeneficiaryError ||
      isTODError ||
      hasSpouseError ||
      hasDobError;
    const disableSubmit = this.checkIfDisableSubmit(isAnyError);

    if (beneficiaries.length === 0) {
      return (
        <Fragment>
          <FixedSubmitFooter
            cancel={this.handleSubmitCancel}
            requestStatusKey="updateBeneficiaries"
            shouldShow={isEditingBeneficiaries}
            shouldDisableSubmit={disableSubmit}
            submit={this.handleSave}
          />
          <CancelConfirmationModal
            cancelPrompt="You haven’t saved your changes."
            cancelTitle="Are you sure you want to leave?"
            onCancel={this.handleCancelConfirmation}
            onContinue={this.handleSubmitCancelConfirmation}
            operationName="updateBeneficiaries"
            showModal={showConfirmationModal}
            continueText="Yes, Leave"
            cancelText="No, Keep Editing"
          />
          <div className="beneficiary-account-type-row">
            <div className="beneficiary-account-type-heading">
              {accountType}
              <AccountOwnersWithPermissions owners={owners} shouldMask />
              <span className="beneficiary-show-investments">
                <button
                  onClick={() => this.toggleDisplay(showAccounts)}
                  className="beneficiary-investments-button">
                  Show Investments
                </button>
              </span>
              {isTOD && (
                <div className="account-type-sub-heading">
                  You must have at least one beneficiary on this account.
                </div>
              )}
              <If test={isPending}>
                <ToolTip tooltipLabel={pendingText}>Updates Pending</ToolTip>
              </If>
              <If
                test={
                  !disableAllActions && isEditable && !beneficiaryLockDisabled()
                }>
                <span className="beneficiary-edit-button-container">
                  <button
                    onClick={() => this.toggleEdit(isEditingBeneficiaries)}
                    disabled={disableEdit}
                    data-testid="beneficiary-add-edit"
                    className="beneficiary-edit-button">
                    Add <span className="pipe">|</span> Edit
                  </button>
                </span>
              </If>
              <If test={isEditable && beneficiaryLockDisabled()}>
                <span className="beneficiary-edit-button-container">
                  <button
                    onClick={() => this.toggleEdit(isEditingBeneficiaries)}
                    data-testid="beneficiary-add-edit-button"
                    disabled={disableEdit}
                    className="beneficiary-edit-button">
                    Add <span className="pipe">|</span> Edit
                  </button>
                </span>
              </If>
            </div>
            <If test={disableAllActions && !beneficiaryLockDisabled() && !isIraAccount}>
              <div className="beneficiary-unable-to-update-text">
                <WarningTriangle className="warning-triangle" /> Beneficiary
                designations must be identical for all identically registered
                accounts. Please{' '}
                <a
                  data-testid="no-bene-contact-us"
                  rel="noreferrer"
                  href={footerLinks.contactUsUrl}
                  target="_blank">
                  contact us
                </a>{' '}
                for support.
              </div>
            </If>
            <If test={disableAllActions && !beneficiaryLockDisabled() && isIraAccount}>
              <div className="beneficiary-unable-to-update-text">
                <WarningTriangle className="warning-triangle" /> Beneficiary
                designations must be identical for all identically registered
                accounts. To make an update, please use this{' '}
                <a
                  data-testid="no-bene-contact-us-isIra"
                  rel="noreferrer"
                  href={footerLinks.iraBeneForm}
                  target="_blank">
                  form
                </a>{' '}
                and mail it back to T. Rowe Price.
              </div>
            </If>
            <If test={disableAllActions && beneficiaryLockDisabled()}>
              <div data-testid="bene-contact-us">
                <Caution /> <strong>Immediate Action Required:</strong>{' '}
                Designated beneficiaries must be consistent for all identically
                registered accounts. Your beneficiaries are not consistent and
                need to be updated. Please update them or{' '}
                <a
                  className="bene-contact-us"
                  rel="noopener noreferrer"
                  href={footerLinks.contactUsUrl}
                  target="_blank">
                  contact us
                </a>{' '}
                for support.
              </div>
            </If>
          </div>
          <div className="beneficiary-table-container">
            <table className="beneficiaries-table">
              <thead>
                <tr>
                  <th>
                    {isEducationSavings
                      ? 'SUCCESSOR BENEFICIARY NAME*'
                      : 'PRIMARY BENEFICIARY NAME*'}
                  </th>
                  <th>Relationship*</th>
                  <th>SSN/TIN</th>
                  <th>Date of Birth</th>
                  <th>% of Benefits</th>
                </tr>
              </thead>
              <tbody>
                {!isEditingBeneficiaries && (
                  <tr className="no-beneficiary-row">
                    <td colSpan="5">
                      <div className="no-beneficiary-text">
                        No beneficiaries on file
                      </div>
                    </td>
                  </tr>
                )}
                {isEditingBeneficiaries && !isEducationSavings && (
                  <tr className="no-beneficiary-row">
                    <td colSpan="5">
                      <div className="add-button-container">
                        <button
                          onClick={this.addNewPrimary}
                          className="beneficiary-add-primary-button">
                          + Add Primary Beneficiary
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {isEditingBeneficiaries && isEducationSavings && (
                  <tr className="no-beneficiary-row">
                    <td colSpan="5">
                      <div className="add-button-container">
                        <button
                          onClick={this.addNewPrimary}
                          className="beneficiary-add-primary-button">
                          + Add Successor Beneficiary
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <If test={showAccounts}>
            <BaseModal
              isOpen={showAccounts}
              hideAnchor
              close={() => this.toggleDisplay(showAccounts)}
              title={`${accountType} Investments`}>
              <div className="beneficiaries-investments-table-container">
                <InvestmentsTable accountsWithBeneficiaries={accounts} />
              </div>
            </BaseModal>
          </If>
        </Fragment>
      );
    }

    return (
      <Fragment>
        <FixedSubmitFooter
          cancel={this.handleSubmitCancel}
          requestStatusKey="updateBeneficiaries"
          shouldShow={isEditingBeneficiaries}
          shouldDisableSubmit={disableSubmit}
          submit={this.handleSave}
        />
        <CancelConfirmationModal
          cancelPrompt="You haven’t saved your changes."
          cancelTitle="Are you sure you want to leave?"
          onCancel={this.handleCancelConfirmation}
          onContinue={this.handleSubmitCancelConfirmation}
          operationName="updateBeneficiaries"
          showModal={showConfirmationModal}
        />
        <div className="beneficiary-account-type-row">
          <div className="beneficiary-account-type-heading">
            {accountType}
            <AccountOwnersWithPermissions owners={owners} shouldMask />
            <span className="beneficiary-show-investments">
              <button
                onClick={() => this.toggleDisplay(showAccounts)}
                className="beneficiary-investments-button">
                Show Investments
              </button>
            </span>
            {isTOD && (
              <div className="account-type-sub-heading">
                You must have at least one beneficiary on this account.
              </div>
            )}
            <If
              test={
                !disableAllActions && isEditable && !beneficiaryLockDisabled()
              }>
              <span className="beneficiary-edit-button-container">
                <button
                  onClick={() => this.toggleEdit(isEditingBeneficiaries)}
                  disabled={disableEdit}
                  className="beneficiary-edit-button">
                  Add <span className="pipe">|</span> Edit
                </button>
              </span>
            </If>
            <If test={isEditable && beneficiaryLockDisabled()}>
              <span className="beneficiary-edit-button-container">
                <button
                  onClick={() => this.toggleEdit(isEditingBeneficiaries)}
                  data-testid="beneficiary-add-edit-button"
                  disabled={disableEdit}
                  className="beneficiary-edit-button">
                  Add <span className="pipe">|</span> Edit
                </button>
              </span>
            </If>
            <If test={isPending}>
              <ToolTip tooltipLabel={pendingText}>Updates Pending</ToolTip>
            </If>
          </div>
          <If test={disableAllActions && !beneficiaryLockDisabled() && !isIraAccount}>
            <div className="beneficiary-unable-to-update-text">
              <WarningTriangle className="warning-triangle" /> Beneficiary
              designations must be identical for all identically registered
              accounts. Please{' '}
              <a
                href={footerLinks.contactUsUrl}
                target="_blank"
                rel="noreferrer">
                contact us
              </a>{' '}
              for support.
            </div>
          </If>
          <If test={disableAllActions && !beneficiaryLockDisabled() && isIraAccount}>
            <div className="beneficiary-unable-to-update-text">
              <WarningTriangle className="warning-triangle" /> Beneficiary
              designations must be identical for all identically registered
              accounts. To make an update, please use this{' '}
              <a
                data-testid="no-bene-contact-us"
                rel="noreferrer"
                href={footerLinks.iraBeneForm}
                target="_blank">
                form
              </a>{' '}
              and mail it back to T. Rowe Price.
            </div>
          </If>
          <If test={disableAllActions && beneficiaryLockDisabled()}>
            <div data-testid="bene-contact-us">
              <Caution /> <strong>Immediate Action Required:</strong> Designated
              beneficiaries must be consistent for all identically registered
              accounts. Your beneficiaries are not consistent and need to be
              updated. Please update them or{' '}
              <a
                className="bene-contact-us"
                rel="noopener noreferrer"
                href={footerLinks.contactUsUrl}
                target="_blank">
                contact us
              </a>{' '}
              for support.
            </div>
          </If>
        </div>
        <div className="beneficiary-table-container">
          <table className="beneficiaries-table">
            <thead>
              <tr>
                <th>
                  {isEducationSavings
                    ? 'SUCCESSOR BENEFICIARY NAME*'
                    : 'PRIMARY BENEFICIARY NAME*'}
                </th>
                <th>Relationship*</th>
                <th>SSN/TIN</th>
                <th>Date of Birth</th>
                <th className="distribution-header">% of Benefits</th>
              </tr>
            </thead>
            <tbody>
              <tr className="mobile-beneficiaries-header">
                <th colSpan="5">
                  {isEducationSavings
                    ? 'SUCCESSOR BENEFICIARIES'
                    : 'PRIMARY BENEFICIARIES'}
                </th>
              </tr>
              {primaryBeneficiaryError && (
                <tr className="error-message-row">
                  <td colSpan="5">
                    <div className="beneficiary-error-text">
                      <WarningCircle className="warning-circle-icon" />
                      {primaryBeneficiaryErrorText}
                    </div>
                  </td>
                </tr>
              )}
              {educationSavingsAgeWarning && (
                <tr className="error-message-row">
                  <td colSpan="5">
                    <div className="esa-warning-container">
                      <WarningTriangle className="warning-triangle" />
                      <span
                        className="esa-warning-text"
                        dangerouslySetInnerHTML={{
                          __html: contentConverter.makeHtml(
                            Content.esaAgeLimitText
                          )
                        }}
                      />
                    </div>
                  </td>
                </tr>
              )}
              {primaryBeneficiaries.map((beneficiary, idx) => {
                if (beneficiary.deleted) {
                  return (
                    <DeletedRow
                      undoDelete={this.handleUndoDelete}
                      beneficiary={beneficiary}
                      key={idx}
                      idx={idx}
                    />
                  );
                }
                return (
                  <BeneficiaryRow
                    beneficiary={beneficiary}
                    isEditing={isEditingBeneficiaries}
                    handleFieldChangeEvent={this.handleFieldChangeEvent}
                    handleSelectFieldChangeEvent={
                      this.handleSelectFieldChangeEvent
                    }
                    handleDistributionChangeEvent={
                      this.handleDistributionChangeEvent
                    }
                    handleDeleteBeneficiary={this.handleDeleteBeneficiary}
                    hasValidationError={this.hasValidationError}
                    isDistributionError={!primaryDistributionError.isValid}
                    hasSpouseError={hasSpouseError}
                    isEducationSavings={isEducationSavings}
                    key={'primary-' + idx}
                  />
                );
              })}
              {!primaryDistributionError.isValid && (
                <tr className="error-message-row">
                  <td colSpan="5">
                    <div className="error-text">
                      <WarningCircle className="warning-circle-icon" />
                      {primaryDistributionError.errorMessage}
                    </div>
                  </td>
                </tr>
              )}
              {secondaryBeneficiaries.length > 0 && (
                <Fragment>
                  <tr className="mobile-beneficiaries-header">
                    <th colSpan="5">SECONDARY BENEFICIARIES</th>
                  </tr>
                  <tr className="secondary-beneficiaries-heading">
                    <th colSpan="5">SECONDARY BENEFICIARY NAME</th>
                  </tr>
                  {secondaryBeneficiaries.map((beneficiary, idx) => {
                    if (beneficiary.deleted) {
                      return (
                        <DeletedRow
                          undoDelete={this.handleUndoDelete}
                          beneficiary={beneficiary}
                          key={idx}
                          idx={idx}
                        />
                      );
                    }
                    return (
                      <BeneficiaryRow
                        beneficiary={beneficiary}
                        isEditing={isEditingBeneficiaries}
                        key={'secondary-' + idx}
                        handleFieldChangeEvent={this.handleFieldChangeEvent}
                        handleSelectFieldChangeEvent={
                          this.handleSelectFieldChangeEvent
                        }
                        handleDistributionChangeEvent={
                          this.handleDistributionChangeEvent
                        }
                        handleDeleteBeneficiary={this.handleDeleteBeneficiary}
                        hasValidationError={this.hasValidationError}
                        isDistributionError={
                          !secondaryDistributionError.isValid
                        }
                        hasSpouseError={hasSpouseError}
                      />
                    );
                  })}
                </Fragment>
              )}
              {!secondaryDistributionError.isValid && (
                <tr className="error-message-row">
                  <td colSpan="5">
                    <div className="error-text">
                      <WarningCircle className="warning-circle-icon" />
                      {secondaryDistributionError.errorMessage}
                    </div>
                  </td>
                </tr>
              )}
              {hasSpouseError && (
                <tr className="error-message-row">
                  <td className="spouse-error" colSpan="5">
                    <div className="error-text">
                      <WarningCircle className="warning-circle-icon" />
                      You may only have one spouse as a beneficiary, please
                      change the relationship to continue.
                    </div>
                  </td>
                </tr>
              )}
              {isEditingBeneficiaries && !isEducationSavings && (
                <tr className="no-beneficiary-row">
                  <td colSpan="5">
                    <div className="add-button-container">
                      <button
                        onClick={this.addNewPrimary}
                        className="beneficiary-add-primary-button">
                        + Add Primary Beneficiary
                      </button>
                      <button
                        onClick={this.addNewSecondary}
                        className="beneficiary-add-secondary-button">
                        + Add Secondary Beneficiary
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {isEditingBeneficiaries && isEducationSavings && (
                <tr className="no-beneficiary-row">
                  <td colSpan="5">
                    <div className="add-button-container">
                      <button
                        onClick={this.addNewPrimary}
                        className="beneficiary-add-primary-button">
                        + Add Successor Beneficiary
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <If test={showAccounts}>
          <BaseModal
            isOpen={showAccounts}
            hideAnchor
            close={() => this.toggleDisplay(showAccounts)}
            title={`${accountType} Investments`}>
            <div className="beneficiaries-investments-table-container">
              <InvestmentsTable accountsWithBeneficiaries={accounts} />
            </div>
          </BaseModal>
        </If>
      </Fragment>
    );
  }
}

BeneficiaryTable.propTypes = {
  accountType: string.isRequired,
  beneficiaries: array,
  accounts: array.isRequired
};

BeneficiaryTable.defaultProps = {
  beneficiaries: []
};

const mapStateToProps = (state) => ({
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  isEditingBeneGlobal: getIsEditingBeneficiaries(state),
  pendingStatusByAccountType: getPendingStatusByAccountType(state),
  customerEmail: getPrimaryEmailAddress(state).address || '',
  customer: getCustomer(state)
});

export default withRouter(connect(mapStateToProps)(BeneficiaryTable));
